import type { ChangeEvent } from 'react';
import type {
  ContentObject,
  RelationWithoutHydration,
} from '../../lib/flotiq-client/types/api-response';

import { Fragment, use, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getFieldErrorMessage } from '../../form-hooks/lib/helpers';
import {
  getObjectDataFromDataUrl,
  getTestProps,
  isRecord,
} from '../../lib/helpers';

import Button from '../../components/Button/Button';
import Checkbox from '../../components/Checkbox/Checkbox';
import Loader from '../../components/Loader/Loader';
import RelationCard from '../../components/RelationCard/RelationCard';

import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useFeaturedImages } from '../../hooks/api/useFeaturedImages';
import { useRelations } from '../../hooks/api/useRelations';

import { ModalInstanceContext } from '../../contexts/ModalContext';

export type ObjectRelationsMap = Record<
  string,
  { relations: RelationWithoutHydration[]; fieldLabel: string }
>;

interface DuplicateRelationsFormProps {
  /**
   * Object relations grouped by field key, with field label and relations array
   */
  objectRelations: ObjectRelationsMap;
  /**
   * On form submission callback, with array of dataUrls that should be duplicated as argument.
   */
  onSubmit: (
    dataUrlsToDuplicate: string[],
  ) => Promise<[Record<string, string | string[]> | undefined, boolean]>;
  testId?: string;
}

const getCheckboxState = (
  relations: RelationWithoutHydration[],
  dataUrlToDuplicate: string[],
) => {
  const isAll = relations.every((item) =>
    dataUrlToDuplicate.includes(item.dataUrl),
  );
  const isAny = relations.some((item) =>
    dataUrlToDuplicate.includes(item.dataUrl),
  );

  return { checked: isAll, indeterminate: !isAll && isAny };
};

const DuplicateRelationsForm = ({
  objectRelations,
  onSubmit,
  testId,
}: DuplicateRelationsFormProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [dataUrlToDuplicate, setDataUrlToDuplicate] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string | string[]>>();

  const onCancel = () => {
    modalInstance.resolve?.();
  };

  const relationsRawData = Array.from(
    Object.values(objectRelations)
      .flatMap(({ relations }) => relations)
      .reduce((acc, { dataUrl }) => {
        acc.add(dataUrl);
        return acc;
      }, new Set<string>()),
  ).map((dataUrl) => ({ dataUrl }));

  const relationsParams = {
    page: 1,
    limit: relationsRawData.length,
  };

  const {
    data: relationsData,
    errors: relationsErrors,
    isLoading: relationsAreLoading,
  } = useRelations(relationsRawData, relationsParams);

  useApiErrorsToast(relationsErrors);

  const contentTypes =
    relationsAreLoading || !relationsData
      ? []
      : Object.values(relationsData).map(({ contentType }) => contentType);

  const [featuredImages, featuredImagesLoading] =
    useFeaturedImages(contentTypes);

  const onMainCheckboxChange = (
    checked: boolean,
    fieldRelations: RelationWithoutHydration[],
  ) => {
    const fieldDataUrls = fieldRelations.map(({ dataUrl }) => dataUrl);
    setDataUrlToDuplicate((prev) =>
      checked
        ? [...prev, ...fieldDataUrls]
        : prev.filter((dataUrl) => !fieldDataUrls.includes(dataUrl)),
    );
  };

  const onSingleCheckboxChange = (checked: boolean, dataUrl: string) => {
    setDataUrlToDuplicate((prev) =>
      checked ? [...prev, dataUrl] : prev.filter((url) => url !== dataUrl),
    );
  };

  const handleSubmit = async () => {
    setIsDuplicating(true);

    const idsToDuplicate = Array.from(
      new Set(
        dataUrlToDuplicate.map(
          (dataUrl: string) => dataUrl.split('/').pop() as string,
        ),
      ),
    );
    const [errors, hasErrors] = await onSubmit(idsToDuplicate);
    setErrors(errors);

    setIsDuplicating(false);
    if (!hasErrors) {
      modalInstance.resolve?.();
    }
  };
  return (
    <>
      <p className="mb-6 whitespace-pre-line text-lg">
        <Trans
          i18nKey="ContentForm.DuplicateDescription"
          components={{ 1: <strong /> }}
        />
      </p>

      {Object.entries(objectRelations).map(
        ([fieldKey, { relations, fieldLabel }]) => {
          const { checked, indeterminate } = getCheckboxState(
            relations,
            dataUrlToDuplicate,
          );

          return (
            <div key={fieldKey} className="mb-4 space-y-4">
              <Checkbox
                name={`${fieldKey}-global`}
                label={fieldLabel}
                checked={checked}
                indeterminate={indeterminate}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onMainCheckboxChange(e.target.checked, relations)
                }
                additionalLabelClasses="text-sm text-slate-400 ml-4"
                {...getTestProps(testId, `${fieldKey}-global`, 'testId')}
              />
              <div
                className="-ml-1.5 md:ml-9 space-y-2"
                {...getTestProps(testId, `${fieldKey}-relations`)}
              >
                {relations.map((item, index) => {
                  const [ctdName, id] = getObjectDataFromDataUrl(item.dataUrl);
                  let relations: string[] | ContentObject[] = [];

                  if (relationsData?.[ctdName]) {
                    relations =
                      'data' in relationsData[ctdName]
                        ? relationsData[ctdName]?.data
                        : relationsData[ctdName];
                  }

                  const relation = (relations || []).find((element) =>
                    isRecord(element) && 'id' in element
                      ? element.id === id
                      : element === id,
                  );

                  const relationToShow = isRecord(relation) ? relation : { id };
                  const isSelected = dataUrlToDuplicate.includes(item.dataUrl);

                  const fieldErrors = Object.entries(errors || {})
                    .filter(([errorId]) => id === errorId)
                    .map(([, error]) => error);

                  return (
                    <Fragment key={`${fieldKey}-${relationToShow.id}`}>
                      <RelationCard
                        relation={relationToShow}
                        relationType={ctdName}
                        titleField={
                          relationsData?.[ctdName] &&
                          'titleField' in relationsData[ctdName]
                            ? relationsData[ctdName]?.titleField
                            : undefined
                        }
                        featuredImageUrl={featuredImages[ctdName]}
                        featuredImageLoading={featuredImagesLoading}
                        relationsAreLoading={relationsAreLoading}
                        imageOverlay={
                          <div className="h-full p-1.5 sm:p-2">
                            <Checkbox
                              name={`${fieldKey}-${item.dataUrl}`}
                              checked={isSelected}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onSingleCheckboxChange(
                                  e.target.checked,
                                  item.dataUrl,
                                )
                              }
                              additionalCheckboxClasses="bg-white dark:bg-slate-800"
                              label={
                                <div
                                  className={twMerge(
                                    'py-1 text-xs px-1 sm:px-2 rounded-sm flex items-center border border-blue',
                                    isSelected
                                      ? 'bg-blue text-white dark:text-slate-950'
                                      : 'bg-blue-50 text-blue dark:bg-slate-800 dark:text-white',
                                  )}
                                >
                                  {isSelected
                                    ? t('ContentForm.WillBeDuplicated')
                                    : t('ContentForm.WillRemainShared')}
                                </div>
                              }
                              additionalContainerClasses="h-full flex flex-col xs:flex-row 
                                                        justify-between xs:justify-start xs:items-start"
                              additionalLabelClasses="ml-0 xs:ml-1"
                              {...getTestProps(
                                testId,
                                `${fieldKey}-${id}`,
                                'testId',
                              )}
                            />
                          </div>
                        }
                        additionalContainerClasses={
                          fieldErrors.length > 0
                            ? 'border-red dark:border-red'
                            : ''
                        }
                        isCopyButton={false}
                        withObjectStatus
                      />
                      {fieldErrors.length > 0 && (
                        <div
                          className={twMerge(
                            'text-red text-sm',
                            'mt-1 mb-2 leading-tight text-left',
                          )}
                          {...getTestProps(
                            testId,
                            `${fieldKey}-${index}-error`,
                          )}
                        >
                          <p className="font-bold mb-1.5">
                            {t('ContentForm.DuplicateWithout')}
                          </p>
                          <div>
                            {fieldErrors.map((value) => {
                              const errorMessage = getFieldErrorMessage(value);
                              const [key, error] = errorMessage.split(':');
                              return (
                                <p key={key}>
                                  {key && (
                                    <span className="font-bold">{key}:</span>
                                  )}
                                  {error}
                                </p>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          );
        },
      )}

      <div
        className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-2
                    border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 z-10"
      >
        <Button
          buttonColor="blueBordered"
          onClick={onCancel}
          disabled={isDuplicating}
        >
          {t('Global.Cancel')}
        </Button>

        <Button
          buttonColor="blue"
          disabled={isDuplicating}
          onClick={handleSubmit}
          iconImage={
            isDuplicating ? (
              <Loader size="small" type="spinner-grid" />
            ) : undefined
          }
          {...getTestProps(testId, 'submit', 'testId')}
        >
          {t('ContentForm.Duplicate', { contentTypeName: '' })}
        </Button>
      </div>
    </>
  );
};

export default DuplicateRelationsForm;
